import React, { useState, useEffect } from 'react';
import { useTruthQuestions, useDareQuestions, useGameQuestions } from '../../hooks/useGameQuestions';
import { useTranslation } from 'react-i18next';
import { loadLanguage } from '../../lib/i18n';
import FlashCard from './Flashcard';
import { Flag } from 'lucide-react';
import ReportModal from '../modals/ReportModal';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../lib/SettingsContext';
import { integratedLanguageService } from '../../services/integratedLanguageService';
import './TruthDareCard.css';

type SupportedLanguage = 'en' | 'es' | 'fr' | 'hi' | 'de';

interface TruthDareCardProps {
  onComplete?: (gameResultCandidate?: string) => void;
  isVisible?: boolean;
  language?: SupportedLanguage;
  spinnerDimension?: number; // Add spinner dimension prop
  winnerName?: string; // Add winner name prop
}

const TruthDareCard: React.FC<TruthDareCardProps> = ({ 
  onComplete, 
  isVisible = true, 
  language = 'en',
  spinnerDimension = 300, // Default spinner dimension
  winnerName = "Winner" // Default winner name
}) => {
  const { t, i18n, ready } = useTranslation();
  const { user } = useAuth();
  const { audioEnabled } = useSettings();
  const [selectedType, setSelectedType] = useState<'truth' | 'dare' | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
  const [showQuestion, setShowQuestion] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [selectedAction, setSelectedAction] = useState<'report' | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isSavingActions, setIsSavingActions] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>('en');
  const prevLanguageRef = React.useRef<SupportedLanguage | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [clickedButton, setClickedButton] = useState<'winner' | 'loser' | null>(null);
  const winnerAudioRef = React.useRef<HTMLAudioElement | null>(null);
  const loserAudioRef = React.useRef<HTMLAudioElement | null>(null);

  // Initialize audio elements
  const initializeAudio = (ref: React.MutableRefObject<HTMLAudioElement | null>, src: string) => {
    if (!ref.current) {
      ref.current = new Audio(src);
      ref.current.loop = false;
    }
    return ref.current;
  };

  // Calculate circle diameter (85% of spinner dimension, same as in Spinner.tsx)
  const circleDiameter = spinnerDimension * 0.85;
  
  // Adjust sizing for smaller screens to prevent wrapping
  const isSmallScreen = windowWidth <= 480;
  const isVerySmallScreen = windowWidth <= 360;
  
  // Responsive sizing factors
  const widthFactor = isVerySmallScreen ? 0.2 : isSmallScreen ? 0.25 : 0.3;
  const heightFactor = isVerySmallScreen ? 0.1 : isSmallScreen ? 0.12 : 0.14;
  const gapFactor = isVerySmallScreen ? 0.08 : isSmallScreen ? 0.09 : 0.10; // Reduced gaps: 10% desktop, 9% tablet
  const fontSizeFactor = isVerySmallScreen ? 0.025 : isSmallScreen ? 0.028 : 0.033;

  // Set current language
  useEffect(() => {
    const lang = language || 'en';
    setCurrentLanguage(lang);
  }, [language]);

  // Handle language change
  useEffect(() => {
    const lang = language || 'en';
    if (prevLanguageRef.current && prevLanguageRef.current !== lang) {
      // Language changed, use integrated service to handle both translations and game data
      integratedLanguageService.changeLanguage(lang, 'truthDare').catch(error => {
        console.error('Error changing language in TruthDareCard:', error);
      });
    }
    prevLanguageRef.current = lang;
  }, [currentLanguage, language]);

  // Ensure language translations are loaded
  useEffect(() => {
    const lang = language || 'en';
    if (lang !== 'en') {
      loadLanguage(lang).catch(error => {
        console.error('Error loading language translations:', error);
      });
    }
  }, [language]);

  // CDN-based question loading for Truth and Dare
  // Use separate truth and dare CDN collections for proper question separation
  const { 
    currentQuestion: truthQuestion, 
    getNextQuestion: getNextTruthQuestion, 
    isLoading: isTruthLoading, 
    error: truthError,
    stats: truthStats,
    initialize: initializeTruth
  } = useGameQuestions({ 
    gameType: 'truth', // Use dedicated truth CDN collection
    language: currentLanguage, 
    userId: user?.uid || null, 
    isVisible, 
    autoInitialize: false 
  });

  const { 
    currentQuestion: dareQuestion, 
    getNextQuestion: getNextDareQuestion, 
    isLoading: isDareLoading, 
    error: dareError,
    stats: dareStats,
    initialize: initializeDare
  } = useGameQuestions({ 
    gameType: 'dare', // Use dedicated dare CDN collection
    language: currentLanguage, 
    userId: user?.uid || null, 
    isVisible, 
    autoInitialize: false 
  });

  // Get the appropriate stats based on selected type
  const getCurrentStats = () => {
    if (selectedType === 'truth') return truthStats;
    if (selectedType === 'dare') return dareStats;
    return truthStats; // Default to truth stats when no type selected
  };

  const cdnStats = getCurrentStats();
  const cdnError = selectedType === 'truth' ? truthError : dareError;
  const isCDNLoading = selectedType === 'truth' ? isTruthLoading : isDareLoading;

  // Initialize managers if they haven't been preloaded during spin
  useEffect(() => {
    if (isVisible) {
      console.log('🎯 Truth & Dare: Checking if managers need initialization...');
      // Initialize both managers if they haven't been preloaded
      initializeTruth();
      initializeDare();
    }
  }, [isVisible, initializeTruth, initializeDare]);

  const handleButtonClick = async (type: 'truth' | 'dare') => {
    setSelectedType(type);
    setIsLoading(true);
    setShowQuestion(false);

    try {
      // Use preloaded CDN-based question from the appropriate collection
      const question = type === 'truth' ? getNextTruthQuestion() : getNextDareQuestion();
      
      if (question) {
        console.log(`🎯 Truth & Dare: Loaded ${type} question from CDN`);
        setCurrentQuestion(question.question || t('games.noQuestionsAvailable'));
      } else {
        console.log(`🎯 Truth & Dare: No ${type} questions available, using fallback`);
        // Fallback questions
        const fallbackQuestions = {
          truth: [
            "What's the most embarrassing thing that happened to you in school?",
            "What's your biggest fear?",
            "What's the worst lie you've ever told?",
            "What's your most embarrassing childhood memory?",
            "What's the most trouble you've ever been in?"
          ],
          dare: [
            "Dance for 30 seconds without music",
            "Sing your favorite song out loud",
            "Do your best impression of someone in the room",
            "Tell a joke and make everyone laugh",
            "Do 10 jumping jacks"
          ]
        };
        
        const fallbackArray = fallbackQuestions[type];
        const randomFallback = fallbackArray[Math.floor(Math.random() * fallbackArray.length)];
        setCurrentQuestion(randomFallback);
      }
    } catch (error) {
      console.error('Error loading question:', error);
      setCurrentQuestion(type === 'truth' ? 'Tell us your biggest secret!' : 'Do something funny!');
    } finally {
      setIsLoading(false);
      setShowQuestion(true);
    }
  };

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCardFlip = () => {
    // Only allow flipping if we're on the color card (not flipped yet)
    if (!isFlipped) {
      setIsFlipped(true);
    }
    // If already flipped to task card, don't allow flipping back
  };

  // Handle report button click
  const handleReportClick = () => {
    setShowReportModal(true);
    setSelectedAction(null); // Reset action selection when opening modal
  };

  // Handle report submission
  const handleReportSubmitted = () => {
    // Report submitted successfully
    // Set the selectedAction to 'report' to show the button as filled
    setSelectedAction('report');
    // You can add any additional logic here, like showing a success message
  };

  // Save like/dislike actions to Firestore
  const saveUserActions = async () => {
    if (!currentQuestion || !user || !selectedType) return;
    
    try {
      setIsSavingActions(true);
      
      // Use the correct collection names based on selected type
      const collectionName = selectedType === 'truth' 
        ? `truthV1_${currentLanguage}` 
        : `dareV1_${currentLanguage}`;
      
      // Note: We need the question ID for this to work properly
      // For now, we'll skip saving if we don't have an ID
      console.log('Would save action to:', collectionName);
      
    } catch (error) {
      console.error('Error saving user actions:', error);
    } finally {
      setIsSavingActions(false);
    }
  };

  const handleReset = () => {
    setSelectedType(null);
    setCurrentQuestion('');
    setShowQuestion(false);
    setIsFlipped(false);
    setSelectedAction(null);
    setShowReportModal(false);
  };

  const handleClose = () => {
    // Reset all state to initial values
    setSelectedType(null);
    setCurrentQuestion('');
    setShowQuestion(false);
    setIsFlipped(false);
    setSelectedAction(null);
    setShowReportModal(false);
    
    // Call onComplete to close the card and allow new spin (same as red/green buttons)
    if (onComplete) {
      onComplete('🔘'); // Grey dot for close/skip
    }
  };

  if (!isVisible) {
    return null;
  }

  // Calculate card size with standard aspect ratio and responsive scaling (copied from WhichOneYouAreCard)
  let scale = windowWidth <= 768 ? 1.3 : 1.1;
  // Responsive: use min of viewport width/height for card size
  const maxCardWidth = Math.min(window.innerWidth * 0.95, 400);
  const maxCardHeight = Math.min(window.innerHeight * 0.95, 400);
  // Use a 1.5 aspect ratio, but never exceed viewport
  let cardWidth = maxCardWidth;
  let cardHeight = cardWidth / 1.5;
  if (cardHeight > maxCardHeight) {
    cardHeight = maxCardHeight;
    cardWidth = cardHeight * 1.5;
  }
  // Make card 20% bigger for tablet/desktop
  if (windowWidth > 768) {
    cardWidth = cardWidth * 1.2;
    cardHeight = cardHeight * 1.2;
  }
  const fontSize = cardWidth * 0.045;
  
  // Responsive design variables
  const responsivePad = windowWidth <= 768 ? '8px' : '12px';
  const responsiveGap = windowWidth <= 768 ? '8px' : '12px';
  const actionBarHeight = 48;
  const questionFontSize = fontSize * 0.9;
  const optionFontSize = fontSize * 0.85;

  // Color strip card (front) - button-19 style with TruthEmoji.png/DareEmoji.png images
  const ColorStripCard = (
    <div style={{
      width: '100%',
      height: '100%',
      background: selectedType === 'truth' ? '#2E7D32' : '#f44336',
      border: 0,
      borderRadius: '0.375em',
      boxShadow: selectedType === 'truth' 
        ? '0.2em 0.2em 0.5em rgba(0, 0, 0, 0.47), 0 -0.1em 0 0.1em rgba(0, 0, 0, 0.27), 0 0.1em 0 0.1em rgba(255, 255, 255, 0.27), -0.2em 0 0.2em #2E7D32 inset, 0 0.2em 0.2em rgba(255, 255, 255, 0.27) inset, 0.2em 0 0.2em rgba(255, 255, 255, 0.27) inset, 0 -0.2em 0.2em #2E7D32 inset'
        : '0.2em 0.2em 0.5em rgba(0, 0, 0, 0.47), 0 -0.1em 0 0.1em rgba(0, 0, 0, 0.27), 0 0.1em 0 0.1em rgba(255, 255, 255, 0.27), -0.2em 0 0.2em #b71c1c inset, 0 0.2em 0.2em rgba(255, 255, 255, 0.27) inset, 0.2em 0 0.2em rgba(255, 255, 255, 0.27) inset, 0 -0.2em 0.2em #b71c1c inset',
      color: '#fff',
      cursor: 'pointer',
      fontSize: Math.max(fontSize * 1.2, 18),
      margin: 0,
      padding: '0.75em 1em',
      textShadow: '0 0 0.2em rgba(255, 255, 255, 0.47)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      position: 'relative',
      boxSizing: 'border-box'
    }}>
      <div style={{
        position: 'absolute',
        top: '8px',
        left: '8px',
        right: '8px',
        bottom: '8px',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        borderRadius: '0.25em',
        pointerEvents: 'none'
      }}></div>
      <div style={{
        textAlign: 'center',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0px',
        height: '100%',
        justifyContent: 'center'
      }}>
        {/* Winner enclosed in cups */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: Math.max(fontSize * 2.0, 30),
          fontWeight: 'bold',
          color: '#FFD700',
          textShadow: '0 0 10px rgba(255, 215, 0, 0.8), 0 0 20px rgba(255, 215, 0, 0.6), 0 0 30px rgba(255, 215, 0, 0.4)',
          filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))',
          marginBottom: '8px'
        }}>
          <span style={{fontSize: Math.max(fontSize * 1.5, 22.5)}}>🏆</span>
          <span>{winnerName || "Winner"}</span>
          <span style={{fontSize: Math.max(fontSize * 1.5, 22.5)}}>🏆</span>
        </div>

        {/* TruthEmoji.png/DareEmoji.png image with fallback to emojis */}
        <div style={{
          width: Math.max(fontSize * 3.75, 60),
          height: Math.max(fontSize * 3.75, 60),
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))'
        }}>
          <img 
            src={selectedType === 'truth' ? "/images/TruthEmoji.png" : "/images/DareEmoji.png"}
            alt={selectedType === 'truth' ? "Truth" : "Dare"}
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.8), 0 0 8px rgba(255,192,203,0.6), 0 0 12px rgba(255,182,193,0.4))'
            }}
            onError={(e) => {
              // Fallback to emojis if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) {
                fallback.style.display = 'block';
              }
            }}
          />
          <div style={{
            fontSize: Math.max(fontSize * 2.5, 40),
            display: 'none',
            textShadow: '0 0 4px rgba(255,255,255,0.8), 0 0 8px rgba(255,192,203,0.6), 0 0 12px rgba(255,182,193,0.4)',
            filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))'
          }}>{selectedType === 'truth' ? '👼' : '😈'}</div>
        </div>
        
        {/* Truth/Dare title with custom black spades and white shine */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: Math.max(fontSize * 1.6, 24),
          fontWeight: 'bold'
        }}>
          <span style={{
            color: '#FFFFFF',
            fontSize: Math.max(fontSize * 1.8, 24),
            textShadow: '0 0 4px rgba(255,255,255,0.9), 0 0 8px rgba(255,255,255,0.7), 0 0 12px rgba(255,255,255,0.5)',
            filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.5))'
          }}>♦</span>
          <span>{isLoading ? t('games.truthDare.loading') : (selectedType === 'truth' ? t('games.truthDare.truth') : t('games.truthDare.dare'))}</span>
          <span style={{
            color: '#FFFFFF',
            fontSize: Math.max(fontSize * 1.8, 24),
            textShadow: '0 0 4px rgba(255,255,255,0.9), 0 0 8px rgba(255,255,255,0.7), 0 0 12px rgba(255,255,255,0.5)',
            filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.5))'
          }}>♦</span>
        </div>
      </div>
    </div>
  );

  // Task card (back) - similar to Would You Rather with 2 divisions and action strip
  const TaskCard = (
    <div 
      onClick={(e) => {
        e.stopPropagation();
        // Prevent flipping when clicking on task card
      }}
      style={{
        width: '100%',
        height: '100%',
        background: selectedType === 'truth' 
          ? 'linear-gradient(135deg, #f0f8f0 0%, #e8f5e8 50%, #f0f8f0 100%)'
          : 'linear-gradient(135deg, #fff0f0 0%, #ffe8e8 50%, #fff0f0 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        fontWeight: 'bold',
        fontSize: Math.max(fontSize * 0.8, 14),
        color: '#333',
        borderRadius: '20px',
        position: 'relative',
        boxSizing: 'border-box',
        border: '3px solid #e5e7eb',
        boxShadow: '0 8px 32px 0 rgba(26,35,126,0.10), 0 2px 8px 0 rgba(0,0,0,0.08)',
        minHeight: 220,
        overflow: 'hidden',
      }}>
      {/* Question Section (15% of card height) */}
      <div style={{
        flex: '0 0 15%',
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        padding: responsivePad,
        background: selectedType === 'truth' 
          ? 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 50%, #0D4A14 100%)'
          : 'linear-gradient(135deg, #f44336 0%, #d32f2f 50%, #b71c1c 100%)',
        color: 'white',
        textAlign: 'center',
        fontWeight: 600,
        fontSize: `clamp(14px, ${questionFontSize * 1.2}px, 2.2vw)`,
        lineHeight: 1.3,
        overflow: 'hidden',
      }}>
        {selectedType === 'truth' ? t('games.truthDare.truth') : t('games.truthDare.dare')}
      </div>

      {/* Task Section fills remaining space above action bar */}
      <div style={{
        flex: '1 1 0',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        padding: `${responsivePad} ${responsivePad} 8px ${responsivePad}`,
        textAlign: 'center',
        color: '#333',
        fontSize: `clamp(15px, ${optionFontSize * 1.1}px, 2.6vw)`,
        lineHeight: 1.4,
        fontWeight: 600,
      }}>
        <div style={{
          fontSize: `clamp(16px, ${optionFontSize * 1.3}px, 2.8vw)`,
          lineHeight: 1.3,
          maxWidth: '100%',
          wordWrap: 'break-word',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          flex: '1',
        }}>
          {isLoading ? '...' : currentQuestion || 'Question'}
        </div>
        
        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '8px',
          width: '100%',
          justifyContent: 'center',
          padding: '0 8px',
          position: 'relative',
          flexWrap: 'wrap',
          boxSizing: 'border-box',
        }}>
          <div style={{ 
            position: 'relative', 
            width: 'min(200px, calc(50% - 6px))', 
            height: '45px',
            minWidth: '140px',
            flex: '1 1 auto',
          }}>
            <button
            onClick={() => {
              if (clickedButton) return; // Prevent multiple clicks
              
              // Start animation and play sound
              setClickedButton('loser');
              setIsAnimating(true);
              if (audioEnabled) {
                const audio = initializeAudio(loserAudioRef, '/sounds/loser.mp3');
                audio.currentTime = 0;
                audio.play().catch((error: any) => {
                  console.error('Error playing loser sound:', error);
                });
              }
              
              // After 5 seconds, fade out and complete
              setTimeout(() => {
                setIsFadingOut(true);
                setTimeout(() => {
                  // Reset state when action is completed
                  setSelectedType(null);
                  setCurrentQuestion('');
                  setShowQuestion(false);
                  setIsFlipped(false);
                  setSelectedAction(null);
                  setShowReportModal(false);
                  setIsAnimating(false);
                  setIsFadingOut(false);
                  setClickedButton(null);
                  
                  if (onComplete) onComplete('🔴'); // Red dot for "I can't do it"
                }, 500);
              }, 5000);
            }}

            style={{
              background: '#D32F2F',
              color: 'white',
              border: 'none',
              borderRadius: '0.375em',
              padding: '8px 16px',
              fontSize: `clamp(12px, ${optionFontSize * 0.9}px, 2vw)`,
              fontWeight: 600,
              cursor: clickedButton ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0.2em 0.2em 0.5em rgba(211, 47, 47, 0.4), 0 -0.1em 0 0.1em rgba(211, 47, 47, 0.3), 0 0.1em 0 0.1em rgba(255, 255, 255, 0.27), -0.2em 0 0.2em #B71C1C inset, 0 0.2em 0.2em rgba(255, 255, 255, 0.27) inset, 0.2em 0 0.2em rgba(255, 255, 255, 0.27) inset, 0 -0.2em 0.2em #B71C1C inset',
              width: '100%',
              height: '45px',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              textAlign: 'center',
              lineHeight: '1.3',
              position: 'relative',
              overflow: 'visible',
              opacity: clickedButton && clickedButton !== 'loser' ? 0.5 : 1,
              isolation: 'isolate',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5), 0 0 8px rgba(0, 0, 0, 0.3)',
            }}
            onMouseEnter={(e) => {
              if (clickedButton) return;
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0.2em 0.2em 0.5em rgba(0, 0, 0, 0.47), 0 -0.1em 0 0.1em rgba(0, 0, 0, 0.27), 0 0.1em 0 0.1em rgba(255, 255, 255, 0.27), -0.2em 0 0.2em #B71C1C inset, 0 0.2em 0.2em rgba(255, 255, 255, 0.27) inset, 0.2em 0 0.2em rgba(255, 255, 255, 0.27) inset, 0 -0.2em 0.2em #B71C1C inset, 0 4px 8px rgba(211, 47, 47, 0.4)';
            }}
            onMouseLeave={(e) => {
              if (clickedButton) return;
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0.2em 0.2em 0.5em rgba(211, 47, 47, 0.4), 0 -0.1em 0 0.1em rgba(211, 47, 47, 0.3), 0 0.1em 0 0.1em rgba(255, 255, 255, 0.27), -0.2em 0 0.2em #B71C1C inset, 0 0.2em 0.2em rgba(255, 255, 255, 0.27) inset, 0.2em 0 0.2em rgba(255, 255, 255, 0.27) inset, 0 -0.2em 0.2em #B71C1C inset';
            }}
                      >
              <span style={{ fontSize: '1.5em', marginRight: '4px' }}>😨</span>
              {t('games.truthDare.iCantDoIt')}
              {clickedButton === 'loser' && isAnimating && (
                <>
                  {/* Top border */}
                  <div style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '2px',
                    background: 'linear-gradient(to right, transparent, #ffffff, transparent)',
                    animation: 'animate1 1.25s linear infinite',
                    zIndex: 1000,
                    borderRadius: '1px',
                    pointerEvents: 'none'
                  }}></div>
                  {/* Right border */}
                  <div style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    width: '2px',
                    height: '100%',
                    background: 'linear-gradient(to bottom, transparent, #ffffff, transparent)',
                    animation: 'animate2 1.25s linear infinite',
                    animationDelay: '0.625s',
                    zIndex: 1000,
                    borderRadius: '1px',
                    pointerEvents: 'none'
                  }}></div>
                  {/* Bottom border */}
                  <div style={{
                    position: 'absolute',
                    bottom: '0',
                    right: '0',
                    width: '100%',
                    height: '2px',
                    background: 'linear-gradient(to left, transparent, #ffffff, transparent)',
                    animation: 'animate3 1.25s linear infinite',
                    zIndex: 1000,
                    borderRadius: '1px',
                    pointerEvents: 'none'
                  }}></div>
                  {/* Left border */}
                  <div style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '2px',
                    height: '100%',
                    background: 'linear-gradient(to top, transparent, #ffffff, transparent)',
                    animation: 'animate4 1.25s linear infinite',
                    animationDelay: '0.625s',
                    zIndex: 1000,
                    borderRadius: '1px',
                    pointerEvents: 'none'
                  }}></div>
                </>
              )}
            </button>
          </div>
          
          <div style={{ 
            position: 'relative', 
            width: 'min(200px, calc(50% - 6px))', 
            height: '45px',
            minWidth: '140px',
            flex: '1 1 auto',
          }}>
            <button
            onClick={() => {
              if (clickedButton) return; // Prevent multiple clicks
              
              // Start animation and play sound
              setClickedButton('winner');
              setIsAnimating(true);
              if (audioEnabled) {
                const audio = initializeAudio(winnerAudioRef, '/sounds/winner.mp3');
                audio.currentTime = 0;
                audio.play().catch((error: any) => {
                  console.error('Error playing winner sound:', error);
                });
              }
              
              // After 5 seconds, fade out and complete
              setTimeout(() => {
                setIsFadingOut(true);
                setTimeout(() => {
                  // Reset state when action is completed
                  setSelectedType(null);
                  setCurrentQuestion('');
                  setShowQuestion(false);
                  setIsFlipped(false);
                  setSelectedAction(null);
                  setShowReportModal(false);
                  setIsAnimating(false);
                  setIsFadingOut(false);
                  setClickedButton(null);
                  
                  if (onComplete) onComplete('🟢'); // Green dot for "I have done it"
                }, 500);
              }, 5000);
            }}

            style={{
              background: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '0.375em',
              padding: '8px 16px',
              fontSize: `clamp(12px, ${optionFontSize * 0.9}px, 2vw)`,
              fontWeight: 600,
              cursor: clickedButton ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0.2em 0.2em 0.5em rgba(76, 175, 80, 0.4), 0 -0.1em 0 0.1em rgba(76, 175, 80, 0.3), 0 0.1em 0 0.1em rgba(255, 255, 255, 0.27), -0.2em 0 0.2em #2E7D32 inset, 0 0.2em 0.2em rgba(255, 255, 255, 0.27) inset, 0.2em 0 0.2em rgba(255, 255, 255, 0.27) inset, 0 -0.2em 0.2em #2E7D32 inset',
              width: '100%',
              height: '45px',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              textAlign: 'center',
              lineHeight: '1.3',
              position: 'relative',
              overflow: 'visible',
              opacity: clickedButton && clickedButton !== 'winner' ? 0.5 : 1,
              isolation: 'isolate',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5), 0 0 8px rgba(0, 0, 0, 0.3)',
            }}
            onMouseEnter={(e) => {
              if (clickedButton) return;
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0.2em 0.2em 0.5em rgba(0, 0, 0, 0.47), 0 -0.1em 0 0.1em rgba(0, 0, 0, 0.27), 0 0.1em 0 0.1em rgba(255, 255, 255, 0.27), -0.2em 0 0.2em #2E7D32 inset, 0 0.2em 0.2em rgba(255, 255, 255, 0.27) inset, 0.2em 0 0.2em rgba(255, 255, 255, 0.27) inset, 0 -0.2em 0.2em #2E7D32 inset, 0 4px 8px rgba(76, 175, 80, 0.4)';
            }}
            onMouseLeave={(e) => {
              if (clickedButton) return;
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0.2em 0.2em 0.5em rgba(76, 175, 80, 0.4), 0 -0.1em 0 0.1em rgba(76, 175, 80, 0.3), 0 0.1em 0 0.1em rgba(255, 255, 255, 0.27), -0.2em 0 0.2em #2E7D32 inset, 0 0.2em 0.2em rgba(255, 255, 255, 0.27) inset, 0.2em 0 0.2em rgba(255, 255, 255, 0.27) inset, 0 -0.2em 0.2em #2E7D32 inset';
            }}
                      >
              <span style={{ fontSize: '1.5em', marginRight: '4px' }}>😤</span>
              {t('games.truthDare.iHaveDoneItProud')}
              {clickedButton === 'winner' && isAnimating && (
                <>
                  {/* Top border */}
                  <div style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '2px',
                    background: 'linear-gradient(to right, transparent, #ffffff, transparent)',
                    animation: 'animate1 1.25s linear infinite',
                    zIndex: 1000,
                    borderRadius: '1px',
                    pointerEvents: 'none'
                  }}></div>
                  {/* Right border */}
                  <div style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    width: '2px',
                    height: '100%',
                    background: 'linear-gradient(to bottom, transparent, #ffffff, transparent)',
                    animation: 'animate2 1.25s linear infinite',
                    animationDelay: '0.625s',
                    zIndex: 1000,
                    borderRadius: '1px',
                    pointerEvents: 'none'
                  }}></div>
                  {/* Bottom border */}
                  <div style={{
                    position: 'absolute',
                    bottom: '0',
                    right: '0',
                    width: '100%',
                    height: '2px',
                    background: 'linear-gradient(to left, transparent, #ffffff, transparent)',
                    animation: 'animate3 1.25s linear infinite',
                    zIndex: 1000,
                    borderRadius: '1px',
                    pointerEvents: 'none'
                  }}></div>
                  {/* Left border */}
                  <div style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '2px',
                    height: '100%',
                    background: 'linear-gradient(to top, transparent, #ffffff, transparent)',
                    animation: 'animate4 1.25s linear infinite',
                    animationDelay: '0.625s',
                    zIndex: 1000,
                    borderRadius: '1px',
                    pointerEvents: 'none'
                  }}></div>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Action Bar (10% of card height, min/max for usability) */}
      <div
        style={{
          flex: '0 0 15%',
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          boxSizing: 'border-box',
          gap: 0,
          borderTop: '1.5px solid #e5e7eb',
          background: 'rgba(255,255,255,0.98)',
          position: 'relative',
          minHeight: '32px',
          maxHeight: 'none',
          zIndex: 10,
        }}
      >
        {/* Left: Action buttons */}
        {/* Left: Slim Report button */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          <button
            onClick={clickedButton ? undefined : handleReportClick}
            style={{
              background: '#e5e7eb',
              border: '1px solid #9ca3af',
              borderRadius: '4px',
              color: '#374151',
              fontSize: '0.75rem',
              cursor: clickedButton ? 'not-allowed' : 'pointer',
              outline: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '3px',
              transition: 'all 0.2s',
              padding: '3px 6px',
              minWidth: '40px',
              minHeight: '20px',
              opacity: clickedButton ? 0.5 : 1,
            }}
            title={t('common.report') || 'Report'}
          >
            <Flag 
              size={12} 
              style={{ 
                fill: selectedAction === 'report' ? '#dc2626' : 'none',
                stroke: selectedAction === 'report' ? '#dc2626' : '#374151'
              }} 
            />
            <span style={{ fontWeight: '400', fontSize: '0.7rem' }}>
              {t('common.report') || 'Report'}
            </span>
          </button>
        </div>

        {/* Right: Skip button */}
        <button
          onClick={clickedButton ? undefined : handleClose}
          style={{
            background: 'none',
            border: 'none',
            color: clickedButton ? '#9ca3af' : '#2563eb',
            fontSize: `clamp(10px, ${fontSize * 0.8}px, 1.4vw)`,
            cursor: clickedButton ? 'not-allowed' : 'pointer',
            padding: '6px 8px',
            borderRadius: '6px',
            fontWeight: 600,
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            opacity: clickedButton ? 0.5 : 1,
          }}
                      title={clickedButton ? 'Decision made' : (t('common.skip') || 'Skip')}
        >
          <span style={{fontWeight:'bold',fontSize:'1.3em',verticalAlign:'middle'}}>⏭</span>
                      {t('common.skip') || 'Skip'}
        </button>
      </div>
    </div>
  );

  if (showQuestion && currentQuestion) {
    return (
      <>
        {/* Modal overlay to block background interaction */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.3)',
            zIndex: 999,
            pointerEvents: 'auto',
          }}
        />
        
        <div className="truth-dare-card positioned">
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: cardWidth,
            height: cardHeight,
            zIndex: 1000,
            opacity: isFadingOut ? 0 : 1,
            transition: 'opacity 0.5s ease-out',
          }}>
            <FlashCard 
              front={ColorStripCard}
              back={TaskCard}
              isFlipped={isFlipped}
              onFlip={handleCardFlip}
              width={cardWidth}
              height={cardHeight}
            />
          </div>
          {/* Action buttons */}
          <div style={{
            position: 'absolute',
            bottom: '-60px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '1rem',
            opacity: isFadingOut ? 0 : 1,
            transition: 'opacity 0.5s ease-out',
            pointerEvents: isFadingOut ? 'none' : 'auto'
          }}>
            <button 
              className="glass-button close-button"
              onClick={clickedButton ? undefined : handleClose}
              style={{ 
                width: 'clamp(100px, 20vw, 120px)', 
                height: 'clamp(35px, 8vw, 40px)', 
                fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
                cursor: clickedButton ? 'not-allowed' : 'pointer',
                opacity: clickedButton ? 0.5 : 1,
                background: selectedType === 'truth' 
                  ? 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 50%, #0D4A14 100%)'
                  : selectedType === 'dare'
                  ? 'linear-gradient(135deg, #f44336 0%, #d32f2f 50%, #b71c1c 100%)'
                  : 'linear-gradient(135deg, #6b7280 0%, #4b5563 50%, #374151 100%)'
              }}
            >
              <div className="inner">
                <div className="top-white"></div>
                <div className="text">{t('common.close') || 'Close'}</div>
              </div>
            </button>
          </div>
        </div>

        {/* Report Modal */}
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          gameType="truth-dare"
          questionId=""
          questionText={currentQuestion}
          questionType={selectedType || undefined}
          onReportSubmitted={handleReportSubmitted}
        />

        {/* CDN Stats Overlay - Show only in development and localhost */}
        {(process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') && (
          <div style={{
            position: 'fixed',
            top: '64px', // 54px header height + 10px margin
            right: '10px',
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '10px',
            borderRadius: '5px',
            fontSize: '12px',
            fontFamily: 'monospace',
            zIndex: 2000,
            maxWidth: '300px'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Truth & Dare CDN Stats:</div>
            {selectedType ? (
              // Show stats for selected type
              <>
                <div style={{ fontWeight: 'bold', color: '#4CAF50' }}>Selected: {selectedType.toUpperCase()}</div>
                <div>Current Questions: {cdnStats?.currentQuestions ?? 0}</div>
                <div>Used Questions: {cdnStats?.usedQuestions ?? 0}</div>
                <div>Prefetch Block: {cdnStats?.nextPrefetchBlock ?? '-'}</div>
                <div>Current Block: {cdnStats?.currentBlock ?? 0}</div>
                <div>Total Blocks: {cdnStats?.totalBlocks ?? 0}</div>
              </>
            ) : (
              // Show stats for both collections
              <>
                <div style={{ fontWeight: 'bold', color: '#FF9800' }}>TRUTH Collection:</div>
                <div>Current: {truthStats?.currentQuestions ?? 0} | Used: {truthStats?.usedQuestions ?? 0} | Block: {truthStats?.currentBlock ?? 0}</div>
                <div style={{ fontWeight: 'bold', color: '#E91E63' }}>DARE Collection:</div>
                <div>Current: {dareStats?.currentQuestions ?? 0} | Used: {dareStats?.usedQuestions ?? 0} | Block: {dareStats?.currentBlock ?? 0}</div>
                <div style={{ marginTop: '5px', fontSize: '10px', color: '#888' }}>
                  Both collections preloaded - click Truth or Dare to select
                </div>
              </>
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {/* Modal overlay to block background interaction */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          zIndex: 999,
          pointerEvents: 'auto',
        }}
      />
      
      <div 
        className="truth-dare-card positioned"
        style={{
          '--spinner-dimension': `${spinnerDimension}px`,
          '--circle-diameter': `${circleDiameter}px`,
          '--button-width': `${Math.max(60, circleDiameter * widthFactor)}px`,
          '--button-height': `${Math.max(40, circleDiameter * heightFactor)}px`,
          '--button-font-size': `${Math.max(12, circleDiameter * fontSizeFactor)}px`,
          '--button-gap': `${Math.max(16, circleDiameter * gapFactor)}px`,
          '--button-border-radius': `${Math.max(4, circleDiameter * 0.015)}px`,
        } as React.CSSProperties}
      >

        
        <div className="buttons-container">
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{t('games.truthDare.loading')}</p>
            </div>
          ) : (
            <>
              <button 
                className="glass-button truth-button"
                onClick={() => handleButtonClick('truth')}
                disabled={isLoading}
              >
                <div className="inner">
                  <div className="top-white"></div>
                  <div className="text">{t('games.truthDare.truth')}</div>
                </div>
              </button>
              <button 
                className="glass-button dare-button"
                onClick={() => handleButtonClick('dare')}
                disabled={isLoading}
              >
                <div className="inner">
                  <div className="top-white"></div>
                  <div className="text">{t('games.truthDare.dare')}</div>
                </div>
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default TruthDareCard; 